import React, { useState, useEffect } from 'react';
import { ListGroup, Card, Badge, Button, Spinner } from 'react-bootstrap';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

const MobilePlanningList = ({
    events = [],
    onEventClick,
    onWeekChange,
    currentWeekStart,
    currentWeekEnd,
    isLoading: parentLoading = false,
    onNewPlanning
}) => {
    const { t } = useTranslation();
    const [selectedDate, setSelectedDate] = useState(moment().format("YYYY-MM-DD"));
    const [isLoading, setIsLoading] = useState(false);
    // const [showViewDropdown, setShowViewDropdown] = useState(false);
    const [currentWeekRange, setCurrentWeekRange] = useState({
        start: moment().startOf("isoWeek"),
        end: moment().endOf("isoWeek")
    });

    // Check if user is employee
    const isEmployeeUser = () => {
        const userRole = localStorage.getItem('UserRole');
        const isEmployee = sessionStorage.getItem('isEmployeeView');
        return userRole === 'Employee' || isEmployee === 'true';
    };

    useEffect(() => {
        toast.info(t("mobileExperienceWarning"), {
            position: "top-center",
            autoClose: 5000,
            toastId: "mobile-warning",
            className: "text-white"
        });
    }, []);

    // Update week range when props change
    useEffect(() => {
        if (currentWeekStart && currentWeekEnd) {
            const weekStart = moment(currentWeekStart);
            const weekEnd = moment(currentWeekEnd);

            if (!weekStart.isSame(currentWeekRange.start) || !weekEnd.isSame(currentWeekRange.end)) {
                setCurrentWeekRange({
                    start: weekStart,
                    end: weekEnd
                });

                // Reset selected date to the first day of the new week
                const today = moment();
                const defaultDate = today.isBetween(weekStart, weekEnd, 'day', '[]')
                    ? today
                    : weekStart;

                setSelectedDate(defaultDate.format("YYYY-MM-DD"));

            }
        }
    }, [currentWeekStart, currentWeekEnd]);

    // Safely get week start and end as moment objects
    const weekStart = moment.isMoment(currentWeekRange.start)
        ? currentWeekRange.start.clone()
        : moment(currentWeekRange.start).startOf("isoWeek");

    const weekEnd = moment.isMoment(currentWeekRange.end)
        ? currentWeekRange.end.clone()
        : moment(currentWeekRange.start).endOf("isoWeek");

    // Generate week days array
    const weekDays = Array.from({ length: 7 }).map((_, i) =>
        weekStart.clone().add(i, "day")
    );

    // Filter events for current week
    const weekEvents = events.filter(event => {
        const eventDate = moment(event.start);
        return eventDate.isBetween(weekStart, weekEnd, 'day', '[]');
    });

    // Group events by date
    const groupedEvents = weekEvents.reduce((acc, event) => {
        const date = moment(event.start).format('YYYY-MM-DD');
        if (!acc[date]) {
            acc[date] = [];
        }
        acc[date].push(event);
        return acc;
    }, {});

    // Filter events for selected day
    const selectedDayEvents = groupedEvents[selectedDate] || [];

    const getStatusBadge = (status) => {
        const statusNumberToKey = {
            0: "planned",
            1: "inprogress",
            2: "jobcomplete",
            3: "unabletoaccesslocation",
            4: "customernotavailable",
            5: "equipmentissue",
            6: "cancelledbycustomer",
            7: "incomplete"
        };

        let normalizedStatus = "";
        if (status === null || status === undefined) {
            normalizedStatus = "planned";
        } else if (!isNaN(Number(status))) {
            normalizedStatus = statusNumberToKey[Number(status)] || "planned";
        } else {
            normalizedStatus = status.toString().replace(/\s+/g, "").toLowerCase();
        }

        const colorMap = {
            planned: "primary",
            inprogress: "info",
            jobcomplete: "success",
            unabletoaccesslocation: "warning",
            customernotavailable: "warning",
            equipmentissue: "warning",
            cancelledbycustomer: "danger",
            incomplete: "warning"
        };

        const color = colorMap[normalizedStatus] || "secondary";
        const translatedStatus = t(`orderDetailStatus.${normalizedStatus}`) || t("orderDetailStatus.planned");

        return <Badge bg={color}>{translatedStatus}</Badge>;
    };

    const handlePrevWeek = async () => {
        if (isLoading || parentLoading) return;

        setIsLoading(true);
        const newWeekStart = weekStart.clone().subtract(1, 'weeks');
        const newWeekEnd = weekEnd.clone().subtract(1, 'weeks');

        setCurrentWeekRange({
            start: newWeekStart,
            end: newWeekEnd
        });

        setSelectedDate(newWeekStart.format("YYYY-MM-DD"));

        if (window.calendarApi) {
            window.calendarApi.prev();
        }

        // Trigger parent to fetch new week's data
        if (onWeekChange) {
            try {
                await onWeekChange(newWeekStart.toDate(), newWeekEnd.toDate());
            } catch (error) {
                console.error('Error fetching previous week data:', error);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleNextWeek = async () => {
        if (isLoading || parentLoading) return;

        setIsLoading(true);
        const newWeekStart = weekStart.clone().add(1, 'weeks');
        const newWeekEnd = weekEnd.clone().add(1, 'weeks');

        setCurrentWeekRange({
            start: newWeekStart,
            end: newWeekEnd
        });

        setSelectedDate(newWeekStart.format("YYYY-MM-DD"));

        if (window.calendarApi) {
            window.calendarApi.next();
        }

        // Trigger parent to fetch new week's data
        if (onWeekChange) {
            try {
                await onWeekChange(newWeekStart.toDate(), newWeekEnd.toDate());
            } catch (error) {
                console.error('Error fetching next week data:', error);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleToday = async () => {
        if (isLoading || parentLoading) return;

        setIsLoading(true);
        const today = moment();
        const newWeekStart = today.clone().startOf("isoWeek");
        const newWeekEnd = today.clone().endOf("isoWeek");

        setCurrentWeekRange({
            start: newWeekStart,
            end: newWeekEnd
        });

        setSelectedDate(today.format("YYYY-MM-DD"));

        if (window.calendarApi) {
            window.calendarApi.today();
        }

        // Trigger parent to fetch current week's data
        if (onWeekChange) {
            try {
                await onWeekChange(newWeekStart.toDate(), newWeekEnd.toDate());
            } catch (error) {
                console.error('Error fetching today data:', error);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleDayClick = (dayKey) => {
        setSelectedDate(dayKey);
        if (window.calendarApi) {
            window.calendarApi.gotoDate(dayKey);
        }
    };

    // Format week range display
    const formatWeekRange = () => {
        return `${weekStart.format('DD MMM')} - ${weekEnd.format('DD MMM YYYY')}`;
    };

    // Combined loading state
    const showLoading = isLoading || parentLoading;

    return (
        <div className="mobile-planning-list">
            {/* Week range + dropdown */}
            <div className="d-flex align-items-center border-bottom bg-light px-3 py-2">
                {/* Week range - LEFT */}
                <small className="text-muted fw-bold">
                    {formatWeekRange()}
                </small>
            </div>

            {/* Week day strip */}
            <div className="week-strip bg-white top-0 border-bottom">
                <div className="d-flex justify-content-between align-items-center py-2">
                    <Button
                        variant="link"
                        size="sm"
                        onClick={handlePrevWeek}
                        disabled={showLoading}
                    >
                        <i className="fas fa-chevron-left"></i>
                    </Button>

                    <div className="d-flex flex-grow-1 justify-content-center">
                        {weekDays.map(day => {
                            const dayKey = day.format("YYYY-MM-DD");
                            const isActive = selectedDate === dayKey;
                            const isToday = day.isSame(moment(), 'day');
                            const dayEventsCount = groupedEvents[dayKey]?.length || 0;

                            return (
                                <div
                                    key={dayKey}
                                    className={`week-day flex-fill text-center ${isActive ? "text-primary fw-semibold" : ""}`}
                                    role="button"
                                    onClick={() => handleDayClick(dayKey)}
                                    style={{
                                        cursor: showLoading ? 'not-allowed' : 'pointer',
                                        minWidth: '36px',
                                    }}
                                    disabled={showLoading}
                                >
                                    <div className={`small ${isActive ? "text-primary" : "text-muted"}`}>
                                        {day.format("dd")}
                                    </div>

                                    <div
                                        className={`d-flex align-items-center justify-content-center rounded-circle mx-auto fw-semibold
                                        ${isActive ? "bg-primary text-white" : isToday ? "bg-primary-subtle text-primary" : ""}`}
                                        style={{
                                            width: '32px',
                                            height: '32px',
                                        }}
                                    >
                                        {day.format("D")}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <Button
                        variant="link"
                        size="sm"
                        onClick={handleNextWeek}
                        disabled={showLoading}
                    >
                        <i className="fas fa-chevron-right"></i>
                    </Button>
                </div>
            </div>

            {/* Events List - Show only one loader */}
            <div className="overflow-auto" style={{
                maxHeight: 'calc(100vh - 350px)'
            }}>
                {showLoading ? (
                    <div className="text-center p-4">
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">{t("loading")}</span>
                        </Spinner>
                    </div>
                ) : selectedDayEvents.length === 0 ? (
                    <div className="text-center p-4">
                        <p className="text-muted">{t("noPlanning")}</p>
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={handleToday}
                            disabled={showLoading}
                        >
                            {t("today")}
                        </Button>
                    </div>
                ) : (
                    <Card className="shadow-sm" style={{ marginBottom: 0 }}>
                        <ListGroup variant="flush">
                            {selectedDayEvents
                                .sort((a, b) => {
                                    const aName = a.extendedProps?.rawData?.Cleaner?.Name || '';
                                    const bName = b.extendedProps?.rawData?.Cleaner?.Name || '';

                                    const nameCompare = aName.localeCompare(bName, undefined, { sensitivity: 'base' });
                                    if (nameCompare !== 0) {
                                        return nameCompare;
                                    }

                                    // fallback: keep time order for same employee
                                    return moment(a.start).diff(moment(b.start));
                                })

                                .map((event, index) => {
                                    const raw = event.extendedProps?.rawData;
                                    const status = raw?.Status;
                                    const [taskName = '', clientNameFromTitle = ''] = (event.title || '').split(' - ');
                                    const cleanerName = raw?.Cleaner?.Name || t("unknownEmployee");
                                    // Client name priority:
                                    const clientName =
                                        raw?.Relation?.Name ||
                                        clientNameFromTitle ||
                                        t("unknownClient");
                                    const relation = raw?.Relation;
                                    const location = relation
                                        ? `${relation.StreetName || ''} ${relation.HouseNumber || ''}, ${relation.PostCode || ''} ${relation.City || ''}`.trim()
                                        : clientName;

                                    const isLastItem = index === selectedDayEvents.length - 1;
                                    const isEmployee = isEmployeeUser();

                                    return (
                                        <ListGroup.Item
                                            key={event.id}
                                            className={isLastItem ? "py-3" : "py-3"}
                                            onClick={(e) => {
                                                // Prevent opening if loading
                                                if (showLoading) return;

                                                // Prevent default link behavior
                                                e.preventDefault();
                                                e.stopPropagation();

                                                // Call the onEventClick handler with the event object
                                                if (onEventClick) {
                                                    onEventClick(event);
                                                }
                                            }}
                                            style={{
                                                cursor: (!showLoading && onEventClick) ? 'pointer' : 'default',
                                                paddingBottom: isLastItem ? '1rem' : undefined,
                                                marginBottom: 0,
                                                // Add touch feedback for mobile
                                                WebkitTapHighlightColor: 'rgba(0, 0, 0, 0.05)'
                                            }}
                                        >
                                            {/* Employee + Client row */}
                                            {isEmployee ? (
                                                // Employee view: Show only client name with status
                                                <div className="d-flex justify-content-between align-items-center mb-1">
                                                    <div className="fw-semibold">
                                                        {clientName}
                                                    </div>
                                                    <div>
                                                        {getStatusBadge(status)}
                                                    </div>
                                                </div>
                                            ) : (
                                                // Non-employee view: Original layout
                                                <>
                                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                                        {/* Employee name - LEFT */}
                                                        <div className="fw-semibold">
                                                            {cleanerName}
                                                        </div>

                                                        {/* Client name - RIGHT */}
                                                        <div className="fw-semibold">
                                                            {clientName}
                                                        </div>
                                                    </div>

                                                    {/* Status badge BELOW */}
                                                    <div className="mb-2 text-end">
                                                        {getStatusBadge(status)}
                                                    </div>
                                                </>
                                            )}

                                            <div className="text-muted small mb-1">
                                                {/* <i className="fas fa-user me-2"></i> */}
                                                <i className="fas fa-tasks me-2"></i>
                                                {/* {clientName} */}
                                                {taskName}
                                            </div>

                                            <div className="text-muted small mb-1">
                                                <i className="fas fa-clock me-2"></i>
                                                {moment(event.start).format('HH:mm')} - {moment(event.end).format('HH:mm')}
                                            </div>

                                            {location && (
                                                <div className="text-muted small">
                                                    <i className="fas fa-map-marker-alt me-2"></i>
                                                    {location}
                                                </div>
                                            )}
                                        </ListGroup.Item>
                                    );
                                })}
                        </ListGroup>
                    </Card>
                )}
            </div>
        </div >
    );
};

export default MobilePlanningList;